import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCamera, FaTimes, FaRedo, FaCheck, FaSyncAlt } from 'react-icons/fa';

/**
 * Direct camera capture.
 *
 * Why this exists: a community health worker screening a patient in a clinic
 * has a phone, not a folder of files. Making them save a photo to the gallery
 * and then find it again in a file picker is two extra steps at the exact
 * moment they are holding a patient's foot in one hand.
 *
 * Returns a real File (not a data URL) via onCapture, so every existing upload
 * path works unchanged — the backend cannot tell a capture from a file upload
 * apart from the filename.
 *
 * Notes on the constraints:
 *  - facingMode 'environment' asks for the rear camera, which is the one you
 *    photograph a patient with. Desktops ignore it and give the webcam.
 *  - We request a high ideal resolution because the clinical features that
 *    matter here (microaneurysms in a fundus photo, ulcer margins) are small.
 *    `ideal` rather than `exact` so a low-end device still gets a stream
 *    instead of an OverconstrainedError.
 *  - JPEG at 0.92: lossless PNG triples upload size on a 3G clinic connection
 *    for no diagnostic gain at this capture quality.
 */
const CameraCapture = ({ open, onClose, onCapture, label }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [starting, setStarting] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setStarting(true);
    stopStream();
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser does not support camera capture.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      // Name the actual cause. "Camera failed" sends a health worker to IT
      // when the real fix is one tap in a permission prompt.
      const msg =
        e.name === 'NotAllowedError'
          ? 'Camera permission was denied. Allow camera access for this site, then try again.'
          : e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError'
          ? 'No camera found on this device. Use "Choose file" instead.'
          : e.name === 'NotReadableError'
          ? 'The camera is already in use by another app. Close it and try again.'
          : e.message || 'Could not start the camera.';
      setError(msg);
    } finally {
      setStarting(false);
    }
  }, [facingMode, stopStream]);

  useEffect(() => {
    if (open) {
      start();
    } else {
      stopStream();
      setSnapshot(null);
      setError(null);
    }
    // Releasing the camera on unmount is not optional — a live stream left
    // running keeps the device's camera light on and drains the battery.
    return stopStream;
  }, [open, start, stopStream]);

  const take = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Could not capture the frame. Try again.');
          return;
        }
        setSnapshot({ blob, url: URL.createObjectURL(blob) });
        stopStream();
      },
      'image/jpeg',
      0.92
    );
  };

  const confirm = () => {
    if (!snapshot) return;
    const slug = (label || 'capture').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const file = new File([snapshot.blob], `${slug}-${Date.now()}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    URL.revokeObjectURL(snapshot.url);
    setSnapshot(null);
    onCapture(file);
    onClose();
  };

  const retake = () => {
    if (snapshot) URL.revokeObjectURL(snapshot.url);
    setSnapshot(null);
    start();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
            <FaCamera className="text-purple-400" />
            {label ? `Capture — ${label}` : 'Capture image'}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close camera"
            className="text-gray-400 hover:text-white p-1"
          >
            <FaTimes />
          </button>
        </div>

        <div className="relative bg-black aspect-video flex items-center justify-center">
          {error ? (
            <div className="p-6 text-center">
              <p className="text-sm text-red-300 mb-4">{error}</p>
              <button
                onClick={start}
                className="px-4 py-2 text-sm rounded-md bg-purple-600 hover:bg-purple-500 text-white"
              >
                Retry
              </button>
            </div>
          ) : snapshot ? (
            <img src={snapshot.url} alt="Captured frame" className="max-h-full max-w-full" />
          ) : (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="max-h-full max-w-full"
              />
              {starting && (
                <p className="absolute text-xs text-gray-400">Starting camera…</p>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            Fill the frame with the area of interest. Good, even light; avoid flash glare.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {snapshot ? (
              <>
                <button
                  onClick={retake}
                  className="px-3 py-2 text-sm rounded-md border border-gray-600 text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                >
                  <FaRedo /> Retake
                </button>
                <button
                  onClick={confirm}
                  className="px-4 py-2 text-sm rounded-md bg-purple-600 hover:bg-purple-500 text-white flex items-center gap-2"
                >
                  <FaCheck /> Use this
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() =>
                    setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))
                  }
                  title="Switch camera"
                  className="px-3 py-2 text-sm rounded-md border border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  <FaSyncAlt />
                </button>
                <button
                  onClick={take}
                  disabled={!!error || starting}
                  className="px-5 py-2 text-sm rounded-md bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white flex items-center gap-2"
                >
                  <FaCamera /> Capture
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
